import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Fingerprint, CheckCircle2, XCircle, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatApiError } from '../lib/utils';
import fingerprintService from '../lib/fingerprintService';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABEL = { admin: 'Administradores', barber: 'Barberos', stylist: 'Estilistas' };
const ROLE_BADGE_COLOR = {
  admin: 'bg-purple-100 text-purple-700',
  barber: 'bg-emerald-100 text-emerald-700',
  stylist: 'bg-pink-100 text-pink-700',
};

export default function EnrolamientoPage() {
  const navigate = useNavigate();
  const { selectedBusiness } = useAuth();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [consent, setConsent] = useState(false);
  const [samples, setSamples] = useState([]); // [{template_b64, quality}]
  const [capturing, setCapturing] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [quality, setQuality] = useState(null);
  const [saving, setSaving] = useState(false);
  // Ref mirror of the last live quality so the sample callback can read the
  // freshest value without capturing a stale closure of the `quality` state.
  const qualityRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/fingerprints/eligible-users`);
      setUsers(data || []);
    } catch (err) {
      toast.error('Error al cargar personal');
      logger.error('load users', err);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Init WebSDK
  useEffect(() => {
    let stopSample, stopQuality, stopError, stopDev;
    (async () => {
      try {
        if (!fingerprintService.isSdkLoaded()) {
          toast.error('WebSDK no cargado. Verifica que Fingerprint.WebApi esté disponible en el navegador.');
          return;
        }
        await fingerprintService.init();
        setSdkReady(true);
        stopSample = fingerprintService.on('sample', ({ template_b64 }) => {
          setSamples((prev) => {
            const next = [...prev, { template_b64, quality: qualityRef.current || 0 }];
            if (next.length >= 3) {
              fingerprintService.stop();
              setCapturing(false);
              toast.success('3 capturas completadas. Presiona "Guardar".');
            } else {
              toast.success(`Captura ${next.length}/3 registrada. Retira y coloca de nuevo el dedo.`);
            }
            return next;
          });
        });
        stopQuality = fingerprintService.on('quality', ({ value }) => {
          qualityRef.current = value;
          setQuality(value);
        });
        stopError = fingerprintService.on('error', ({ message }) => toast.error(message || 'Error del lector'));
        stopDev = fingerprintService.on('deviceChange', ({ connected }) => {
          toast[connected ? 'success' : 'warning'](connected ? 'Lector conectado' : 'Lector desconectado');
        });
      } catch (err) {
        logger.error('sdk init', err);
        toast.error('No se pudo iniciar el WebSDK. ¿Está corriendo el Lite Client?');
      }
    })();
    return () => {
      stopSample && stopSample();
      stopQuality && stopQuality();
      stopError && stopError();
      stopDev && stopDev();
      fingerprintService.stop();
    };
  }, []);

  const grouped = users.reduce((acc, u) => {
    (acc[u.role] = acc[u.role] || []).push(u);
    return acc;
  }, {});

  const startCapture = async () => {
    if (!userId) { toast.error('Selecciona un miembro del personal'); return; }
    if (!consent) { toast.error('Debes aceptar el consentimiento LFPDPPP'); return; }
    setSamples([]);
    setCapturing(true);
    try {
      await fingerprintService.start();
      toast.info('Coloca el dedo en el lector (1/3)…');
    } catch (err) {
      setCapturing(false);
      toast.error(err.message || 'No se pudo iniciar la captura');
    }
  };

  const cancelCapture = async () => {
    await fingerprintService.stop();
    setCapturing(false);
    setSamples([]);
  };

  const resetCaptures = async () => {
    await fingerprintService.stop();
    setSamples([]);
    setCapturing(false);
    setQuality(null);
    qualityRef.current = null;
    toast.info('Capturas reiniciadas');
  };

  const saveEnrollment = async () => {
    if (samples.length < 1) { toast.error('Necesitas al menos 1 captura antes de guardar'); return; }
    // Stop any active acquisition before saving so the SDK releases the reader
    await fingerprintService.stop();
    setCapturing(false);
    setSaving(true);
    try {
      // Send the best-quality sample AND all captured templates. The backend
      // stores them all so 1:N matching at check-in can compare against every
      // capture and take the highest score.
      const best = samples.reduce((a, b) => (b.quality > a.quality ? b : a));
      await axios.post(`${API_URL}/fingerprints/enroll`, {
        user_id: userId,
        template_b64: best.template_b64,
        templates_b64: samples.map((s) => s.template_b64).filter(Boolean),
        quality: best.quality || 0,
        consent_accepted: true,
      });
      toast.success('Enrolamiento guardado');
      navigate('/asistencia');
    } catch (err) {
      toast.error(formatApiError(err, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = users.find((u) => u.user_id === userId);

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="enrolamiento-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/asistencia')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Enrolar huella
          </h1>
          <p className="text-slate-500 mt-1">Registra la huella dactilar de un miembro del personal.</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>1 · Selecciona a la persona</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="bg-white border-slate-200" data-testid="user-select">
              <SelectValue placeholder="Seleccionar personal…" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {Object.entries(grouped).map(([role, list]) => (
                <SelectGroup key={role}>
                  <SelectLabel>{ROLE_LABEL[role] || role}</SelectLabel>
                  {list.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} {u.is_enrolled && <span className="text-xs text-emerald-600 ml-1">✓ ya enrolado (sobrescribir)</span>}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>

          {selectedUser && (
            <div className="p-3 rounded-lg bg-slate-50 flex items-center justify-between">
              <div>
                <p className="font-medium">{selectedUser.name}</p>
                <p className="text-xs text-slate-500">{selectedUser.email || '—'}</p>
              </div>
              <Badge variant="outline" className={cn('text-xs', ROLE_BADGE_COLOR[selectedUser.role])}>
                {ROLE_LABEL[selectedUser.role] || selectedUser.role}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>2 · Consentimiento</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-0.5" data-testid="consent-checkbox" />
            <span className="text-sm text-slate-700">
              La persona acepta el tratamiento de su huella dactilar (dato biométrico) exclusivamente para
              control de asistencia interno, conforme a la <b>LFPDPPP</b>. El dato se almacena como template
              matemático encriptado (AES-256), no como imagen, y puede ser eliminado en cualquier momento
              a solicitud del titular.
            </span>
          </label>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>3 · Captura de huella (3 tomas)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => {
              const s = samples[i];
              return (
                <div
                  key={i}
                  className={cn(
                    'rounded-lg border-2 p-4 flex flex-col items-center justify-center h-32 transition',
                    s ? 'border-emerald-400 bg-emerald-50' : 'border-dashed border-slate-300 bg-slate-50',
                  )}
                  data-testid={`sample-slot-${i}`}
                >
                  {s ? (
                    <>
                      <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-1" />
                      <span className="text-xs text-slate-600">Captura {i + 1}</span>
                      <span className="text-[10px] text-slate-400">Calidad: {s.quality || '—'}</span>
                    </>
                  ) : (
                    <>
                      <Fingerprint className={cn(
                        'w-8 h-8 mb-1',
                        capturing && samples.length === i ? 'text-emerald-500 animate-pulse' : 'text-slate-300',
                      )} />
                      <span className="text-xs text-slate-400">Captura {i + 1}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {capturing && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <div className="text-sm text-blue-800">
                Coloca el dedo… ({samples.length}/3)
                {quality != null && <span className="ml-2 text-xs">Calidad live: {quality}</span>}
              </div>
              <Button variant="outline" size="sm" className="ml-auto" onClick={cancelCapture} data-testid="cancel-capture">
                <XCircle className="w-4 h-4 mr-1" /> Cancelar
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            {samples.length > 0 && !capturing && (
              <Button
                variant="outline"
                onClick={resetCaptures}
                data-testid="reset-captures-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reiniciar
              </Button>
            )}
            {!capturing && samples.length < 3 && (
              <Button
                onClick={startCapture}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={!sdkReady || !userId || !consent}
                data-testid="start-capture-btn"
              >
                <Fingerprint className="w-4 h-4 mr-2" />
                {samples.length === 0 ? 'Iniciar captura' : `Continuar (${samples.length}/3)`}
              </Button>
            )}
            {samples.length >= 1 && !capturing && (
              <Button
                onClick={saveEnrollment}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                disabled={saving}
                data-testid="save-enrollment-btn"
                title={samples.length < 3 ? 'Se recomienda 3 capturas, pero puedes guardar con menos.' : ''}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {samples.length < 3
                  ? `Guardar de todos modos (${samples.length}/3)`
                  : 'Guardar enrolamiento'}
              </Button>
            )}
          </div>

          {!sdkReady && (
            <p className="text-xs text-amber-600">
              ⚠ WebSDK no cargado. Instala el DigitalPersona Lite Client y recarga la página.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
